<script setup lang="ts">
// The one "needs you" strip (issue 713) — the lib owns the LOOK, every string is
// passed in. ONE item: its full sentence and its button (a lone legal item is the
// old legal-notice strip word for word, testid included). Two or more: the label
// followed by one link per item. Warning tokens, as the legal strip wore.
import { computed } from 'vue';
import { Button, LumiIcon } from '@lumi/ui';
import type { NeedsYouItemView } from '../utils/needsYouItems';

const props = defineProps<{
  items: NeedsYouItemView[];
  listLabel: string;
  hideLabel: string;
}>();
defineEmits<{ (e: 'hide'): void }>();

const lone = computed(() => (props.items.length === 1 ? props.items[0] : null));
// The legal e2e specs find the lone legal sentence by its long-standing test id.
const testId = computed(() => (lone.value?.key === 'legal_notices' ? 'legal-notice-banner' : 'needs-you-strip'));
</script>

<template>
  <div class="lumi-needs-you" role="status" :data-testid="testId" data-needs-you-strip>
    <span class="lumi-needs-you__icon" aria-hidden="true">
      <LumiIcon name="warning" width="18" height="18" stroke-width="2" />
    </span>
    <div v-if="lone" class="lumi-needs-you__content">
      <p v-if="lone.title" class="lumi-needs-you__title">{{ lone.title }}</p>
      <p class="lumi-needs-you__message">{{ lone.message }}</p>
    </div>
    <a v-if="lone && lone.href" class="lumi-needs-you__cta" :href="lone.href">{{ lone.ctaLabel }}</a>
    <div v-else-if="!lone" class="lumi-needs-you__content lumi-needs-you__list">
      <span class="lumi-needs-you__label">{{ listLabel }}</span>
      <template v-for="item in items" :key="item.key">
        <a v-if="item.href" class="lumi-needs-you__link" :href="item.href" :data-item="item.key">{{ item.linkLabel }}</a>
        <span v-else class="lumi-needs-you__plain" :data-item="item.key">{{ item.linkLabel }}</span>
      </template>
    </div>
    <Button variant="ghost" size="sm" class="lumi-needs-you__hide" @click="$emit('hide')">{{ hideLabel }}</Button>
  </div>
</template>

<style>
/* Tokens only (no tailwind utilities): matches BillingBanner / DemoStageBar —
   these SPAs don't reliably process the warning-* utility classes. Items and the
   hide control wrap on narrow widths; the hide control never leaves the strip. */
.lumi-needs-you{display:flex;align-items:center;flex-wrap:wrap;gap:var(--space-2) var(--space-3);width:100%;padding:var(--space-2) var(--space-4);font-family:var(--font-body);background:var(--warning-soft);color:var(--warning-text);}
.lumi-needs-you__icon{flex:none;display:flex;align-items:center;padding-top:1px;}
.lumi-needs-you__content{flex:1;min-width:12rem;}
.lumi-needs-you__title{font-size:var(--text-sm);font-weight:var(--weight-semibold);margin:0;line-height:1.3;}
.lumi-needs-you__message{font-size:var(--text-sm);margin:0;line-height:1.4;}
.lumi-needs-you__list{display:flex;flex-wrap:wrap;align-items:center;gap:var(--space-1) var(--space-3);font-size:var(--text-sm);}
.lumi-needs-you__label{font-weight:var(--weight-semibold);}
.lumi-needs-you__link{color:inherit;font-weight:var(--weight-semibold);text-decoration:underline;text-underline-offset:2px;}
.lumi-needs-you__cta{flex:none;font-size:var(--text-sm);font-weight:var(--weight-semibold);color:inherit;text-decoration:none;padding:var(--space-1) var(--space-3);border-radius:var(--radius-md);border:1px solid currentColor;white-space:nowrap;}
.lumi-needs-you__cta:hover,.lumi-needs-you__link:hover{opacity:.85;}
.lumi-needs-you .lumi-needs-you__hide{flex:none;margin-left:auto;color:inherit;}
.lumi-needs-you__cta:focus-visible,.lumi-needs-you__link:focus-visible{outline:2px solid currentColor;outline-offset:2px;}
/* On a phone the strip stays pinned above the page beside the billing banner, so
   it takes as little of the screen as it can (BillingBanner.vue does the same). */
@media (max-width:767px){
  .lumi-needs-you{gap:var(--space-1) var(--space-2);padding:var(--space-2) var(--space-3);}
  .lumi-needs-you__title,.lumi-needs-you__message,.lumi-needs-you__list,.lumi-needs-you__cta{font-size:var(--text-xs);}
  .lumi-needs-you__content{min-width:0;flex-basis:calc(100% - 18px - var(--space-2));}
}
</style>
